import {
  genderSchema,
  isoDateSchema,
  nationalIdSchema,
  phoneSchema,
} from '../../../core/primitives/schema';
import { ageInYearsOn } from '../domain/dependent/eligibility';
import type { MyProfileUpdate } from './ports/employeePort';

/**
 * Validation for the profile-completion form.
 *
 * Pure, so the rules a person is held to can be tested without rendering
 * anything (§5.4). The screen renders the result; it does not decide it.
 */

export interface ProfileFormValues {
  readonly nationalId: string;
  readonly birthDate: string;
  readonly gender: string;
  readonly phone: string;
  readonly address: string;
}

export type ProfileFieldErrors = Partial<Record<keyof ProfileFormValues, string>>;

export interface ProfileValidation {
  readonly errors: ProfileFieldErrors;
  readonly valid: boolean;
}

/** Nobody working at a school is younger than this or older than that. */
const MINIMUM_WORKING_AGE = 16;
const MAXIMUM_PLAUSIBLE_AGE = 90;

/**
 * Validates every field and reports all failures at once.
 *
 * Stopping at the first error makes someone fix one thing, submit, and be told
 * about the next — on a phone, over a slow connection, that is how a form gets
 * abandoned.
 */
export function validateProfileForm(
  values: ProfileFormValues,
  today: string,
): ProfileValidation {
  const errors: ProfileFieldErrors = {};

  const nationalId = nationalIdSchema.safeParse(values.nationalId);
  if (!nationalId.success) {
    errors.nationalId = nationalId.error.issues[0]?.message ?? 'رقم الهوية غير صحيح';
  }

  const birthDate = isoDateSchema.safeParse(values.birthDate);
  if (!birthDate.success) {
    errors.birthDate = birthDate.error.issues[0]?.message ?? 'تاريخ الميلاد غير صحيح';
  } else {
    /*
     * A plausibility range, not a rule of the fund.
     *
     * The date picker makes 2026 as easy to type as 1926, and a birth date is
     * copied onto every dependant age calculation afterwards. Catching it here
     * costs a moment; catching it after enrolment costs a correction to
     * someone's coverage.
     */
    const age = ageInYearsOn(birthDate.data, today);
    if (age < MINIMUM_WORKING_AGE || age > MAXIMUM_PLAUSIBLE_AGE) {
      errors.birthDate = 'تاريخ الميلاد غير منطقي. تحقق من السنة.';
    }
  }

  if (!genderSchema.safeParse(values.gender).success) {
    errors.gender = 'اختر الجنس';
  }

  const phone = phoneSchema.safeParse(values.phone);
  if (!phone.success) {
    errors.phone = phone.error.issues[0]?.message ?? 'رقم الجوال غير صحيح';
  }

  if (values.address.trim().length < 3) {
    errors.address = 'أدخل العنوان';
  }

  return { errors, valid: Object.keys(errors).length === 0 };
}

/** Shapes validated values into what the port expects. */
export function toProfileUpdate(values: ProfileFormValues): MyProfileUpdate {
  return {
    nationalId: nationalIdSchema.parse(values.nationalId),
    birthDate: isoDateSchema.parse(values.birthDate),
    gender: genderSchema.parse(values.gender),
    // Normalised, so a number typed with a country code or spaces is stored
    // the same way as one typed bare.
    phone: phoneSchema.parse(values.phone),
    address: values.address.trim(),
  };
}

/**
 * What the employee still has to do before they can submit a declaration.
 *
 * Returned as a list rather than a boolean so the screen can say *what* is
 * missing. "Your profile is incomplete" with no further detail is the kind of
 * message that generates a phone call to HR.
 */
export function outstandingProfileFields(record: {
  readonly nationalId: string | null;
  readonly birthDate: string | null;
  readonly gender: string | null;
  readonly phone: string | null;
  readonly address: string | null;
}): readonly string[] {
  const missing: string[] = [];

  if (!record.nationalId) missing.push('رقم الهوية');
  if (!record.birthDate) missing.push('تاريخ الميلاد');
  if (!record.gender) missing.push('الجنس');
  if (!record.phone) missing.push('رقم الجوال');
  if (!record.address) missing.push('العنوان');

  return missing;
}
